import React, { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { TREATMENT_TYPES, calcRecallDate } from '../utils/data';
import { format, parseISO } from 'date-fns';

const FIELDS_STYLE = {
  display: 'flex', flexDirection: 'column', gap: 6,
};
const LABEL_STYLE = {
  fontSize: 11,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  color: '#1B2B4B',
  fontFamily: "'JetBrains Mono', monospace",
};
const INPUT_STYLE = {
  padding: '9px 11px',
  border: '1px solid #DDDDD8',
  borderRadius: 2,
  fontSize: 13,
  fontFamily: "'JetBrains Mono', monospace",
  color: '#1B2B4B',
  background: '#FAFAF8',
  outline: 'none',
  width: '100%',
  boxSizing: 'border-box',
};

export default function AddPatientPanel({ onClose, onSave, editPatient }) {
  const [form, setForm] = useState({
    firstName: '', lastName: '', dob: '', phone: '', email: '',
    lastVisit: '', treatmentType: 'Routine cleaning', customRecallDate: '', notes: '',
  });
  const [previewDate, setPreviewDate] = useState(null);

  useEffect(() => {
    if (editPatient) {
      setForm({
        firstName: editPatient.firstName || '',
        lastName: editPatient.lastName || '',
        dob: editPatient.dob || '',
        phone: editPatient.phone || '',
        email: editPatient.email || '',
        lastVisit: editPatient.lastVisit || '',
        treatmentType: editPatient.treatmentType || 'Routine cleaning',
        customRecallDate: editPatient.recallDate || '',
        notes: editPatient.notes || '',
      });
    }
  }, [editPatient]);

  useEffect(() => {
    if (form.treatmentType !== 'Custom' && form.lastVisit) {
      const d = calcRecallDate(form.treatmentType, form.lastVisit);
      setPreviewDate(d);
    } else {
      setPreviewDate(null);
    }
  }, [form.treatmentType, form.lastVisit]);

  function set(key, val) { setForm(f => ({ ...f, [key]: val })); }

  function handleSubmit(e) {
    e.preventDefault();
    const recallDate = form.treatmentType === 'Custom'
      ? form.customRecallDate
      : previewDate ? format(previewDate, 'yyyy-MM-dd') : '';
    onSave({
      ...form,
      recallDate,
      ...(editPatient ? { id: editPatient.id, visitHistory: editPatient.visitHistory || [], lastContacted: editPatient.lastContacted || null } : {}),
    });
  }

  return (
    <div style={overlayStyle} onClick={e => e.target === e.currentTarget && onClose()}>
      <div style={panelStyle}>
        <div style={headerStyle}>
          <span style={{ fontFamily: "'JetBrains Mono', monospace", fontWeight: 700, fontSize: 16, color: '#1B2B4B' }}>
            {editPatient ? 'Edit patient' : 'Add patient'}
          </span>
          <button onClick={onClose} style={closeBtnStyle}><X size={18} /></button>
        </div>

        <form onSubmit={handleSubmit} style={{ padding: '24px 28px', overflowY: 'auto', flex: 1 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div style={FIELDS_STYLE}>
              <label style={LABEL_STYLE}>First name *</label>
              <input style={INPUT_STYLE} required value={form.firstName} onChange={e => set('firstName', e.target.value)} placeholder="Sarah" />
            </div>
            <div style={FIELDS_STYLE}>
              <label style={LABEL_STYLE}>Last name *</label>
              <input style={INPUT_STYLE} required value={form.lastName} onChange={e => set('lastName', e.target.value)} placeholder="Mitchell" />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div style={FIELDS_STYLE}>
              <label style={LABEL_STYLE}>Date of birth</label>
              <input style={INPUT_STYLE} type="date" value={form.dob} onChange={e => set('dob', e.target.value)} />
            </div>
            <div style={FIELDS_STYLE}>
              <label style={LABEL_STYLE}>Phone number</label>
              <input style={INPUT_STYLE} type="tel" value={form.phone} onChange={e => set('phone', e.target.value)} placeholder="+65 9000 0000" />
            </div>
          </div>

          <div style={{ marginBottom: 16, ...FIELDS_STYLE }}>
            <label style={LABEL_STYLE}>Email address</label>
            <input style={INPUT_STYLE} type="email" value={form.email} onChange={e => set('email', e.target.value)} placeholder="patient@email.com" />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div style={FIELDS_STYLE}>
              <label style={LABEL_STYLE}>Last visit date *</label>
              <input style={INPUT_STYLE} type="date" required value={form.lastVisit} onChange={e => set('lastVisit', e.target.value)} />
            </div>
            <div style={FIELDS_STYLE}>
              <label style={LABEL_STYLE}>Treatment type *</label>
              <select style={{ ...INPUT_STYLE, cursor: 'pointer' }} required value={form.treatmentType} onChange={e => set('treatmentType', e.target.value)}>
                {Object.keys(TREATMENT_TYPES).map(t => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
          </div>

          {form.treatmentType === 'Custom' && (
            <div style={{ marginBottom: 16, ...FIELDS_STYLE }}>
              <label style={LABEL_STYLE}>Custom recall date *</label>
              <input style={INPUT_STYLE} type="date" required value={form.customRecallDate} onChange={e => set('customRecallDate', e.target.value)} />
            </div>
          )}

          {previewDate && (
            <div style={previewBoxStyle}>
              <span style={{ fontSize: 11, fontWeight: 700, color: '#2D9B8A', textTransform: 'uppercase', letterSpacing: '0.5px', fontFamily: "'JetBrains Mono', monospace" }}>
                Recall date preview
              </span>
              <span style={{ fontSize: 18, fontWeight: 700, color: '#1B2B4B', fontFamily: "'JetBrains Mono', monospace" }}>
                {format(previewDate, 'dd MMM yyyy')}
              </span>
              <span style={{ fontSize: 12, color: '#7A7A72', fontFamily: "'JetBrains Mono', monospace" }}>
                Based on {form.treatmentType} ({TREATMENT_TYPES[form.treatmentType]?.interval}-day interval)
              </span>
            </div>
          )}

         
<div style={{ marginBottom: 16, ...FIELDS_STYLE }}>
  <label style={LABEL_STYLE}>Personalized Line</label>
  <input
    style={INPUT_STYLE}
    value={form.personalizedLine || ''}
    onChange={e => set('personalizedLine', e.target.value)}
    placeholder="e.g., Hope your daughter's graduation was wonderful!"
  />
  <div style={{ fontSize: 11, color: '#7A9A92', marginTop: 4, fontFamily: "'JetBrains Mono', monospace" }}>
    💡 This personalizes each message uniquely for this patient
  </div>
</div>

          <div style={{ marginBottom: 24, ...FIELDS_STYLE }}>
            <label style={LABEL_STYLE}>Notes (optional)</label>
            <textarea
              style={{ ...INPUT_STYLE, resize: 'vertical', minHeight: 72 }}
              value={form.notes}
              onChange={e => set('notes', e.target.value)}
              placeholder="Any notes about this patient..."
            />
          </div>

          <div style={{ display: 'flex', gap: 10 }}>
            <button type="button" onClick={onClose} style={cancelBtnStyle}>Cancel</button>
            <button type="submit" style={saveBtnStyle}>{editPatient ? 'Save changes' : 'Add patient'}</button>
          </div>
        </form>
      </div>
    </div>
  );
}

const overlayStyle = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 1000,
  display: 'flex', justifyContent: 'flex-end',
};
const panelStyle = {
  background: '#fff', width: 480, maxWidth: '100vw',
  height: '100vh', display: 'flex', flexDirection: 'column',
  boxShadow: '-4px 0 24px rgba(0,0,0,0.12)',
};
const headerStyle = {
  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
  padding: '20px 28px', borderBottom: '1px solid #E5E5E0',
};
const closeBtnStyle = {
  background: 'none', border: 'none', cursor: 'pointer',
  color: '#7A7A72', padding: 4, display: 'flex', alignItems: 'center',
};
const previewBoxStyle = {
  background: '#F0F7F6', border: '1px solid #B5DED9', borderRadius: 2,
  padding: '12px 16px', marginBottom: 16, display: 'flex', flexDirection: 'column', gap: 2,
};
const cancelBtnStyle = {
  flex: 1, padding: '11px 0', background: '#fff', color: '#1B2B4B',
  border: '1px solid #DDDDD8', borderRadius: 2, cursor: 'pointer',
  fontSize: 13, fontWeight: 600, fontFamily: "'JetBrains Mono', monospace",
};
const saveBtnStyle = {
  flex: 2, padding: '11px 0', background: '#1B2B4B', color: '#fff',
  border: 'none', borderRadius: 2, cursor: 'pointer',
  fontSize: 13, fontWeight: 600, fontFamily: "'JetBrains Mono', monospace",
};
